import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, BUSINESS } from "@/lib/api";

const SERVICES = [
  "Furnace install / repair",
  "Heat pump install",
  "Air conditioning",
  "Gas fitting",
  "Hot water tank / tankless",
  "CleanBC rebate inquiry",
  "General question / other",
];

const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service || null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your message. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28 cg-pink-bg overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 cg-halftone-light opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-10">
        {/* Left: info */}
        <div className="lg:col-span-2" data-testid="contact-info">
          <div className="eyebrow mb-4">Get in Touch</div>
          <h2 className="font-[Manrope] font-extrabold text-[#0F0F0F] text-3xl sm:text-4xl lg:text-5xl leading-tight mb-5">
            We're Here to Help. Call Us Today.
          </h2>
          <p className="text-[#475569] text-lg mb-8 leading-relaxed">
            Whether it's a new install, a repair, or a rebate question, our team is ready to assist.
          </p>

          <div className="space-y-4 mb-8">
            <a href={BUSINESS.phoneHref} className="flex items-start gap-4 group" data-testid="info-phone">
              <div className="w-12 h-12 rounded-xl bg-[#E50914] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold mb-1">Call us</div>
                <div className="text-[#0F0F0F] font-[Manrope] font-bold text-lg group-hover:text-[#E50914]">
                  {BUSINESS.phone}
                </div>
                <div className="text-xs text-[#64748B]">{BUSINESS.hours}</div>
              </div>
            </a>

            <a href={BUSINESS.emailHref} className="flex items-start gap-4 group" data-testid="info-email">
              <div className="w-12 h-12 rounded-xl bg-[#0F0F0F] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold mb-1">Email us</div>
                <div className="text-[#0F0F0F] font-[Manrope] font-bold text-lg group-hover:text-[#E50914] break-all">
                  {BUSINESS.email}
                </div>
                <div className="text-xs text-[#64748B]">We reply within 24 hours</div>
              </div>
            </a>

            <div className="flex items-start gap-4" data-testid="info-area">
              <div className="w-12 h-12 rounded-xl bg-[#FCE7E9] border-2 border-[#E50914] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#E50914]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold mb-1">Service area</div>
                <div className="text-[#0F0F0F] font-[Manrope] font-bold text-lg">
                  {BUSINESS.serviceArea}
                </div>
                <div className="text-xs text-[#64748B]">Vancouver, Surrey, Burnaby, Abbotsford & more</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-3" data-testid="contact-form-wrapper">
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-[#EAEAEA]">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-20 h-20 rounded-full bg-[#FCE7E9] flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-10 h-10 text-[#E50914]" />
                </div>
                <h3 className="font-[Manrope] font-extrabold text-[#0F0F0F] text-2xl mb-3">
                  Message received!
                </h3>
                <p className="text-[#475569] mb-6 max-w-md mx-auto">
                  Thanks for reaching out. We've logged your request and will be in touch within 24 hours. For emergencies, please call us directly.
                </p>
                <button
                  className="btn-outline-coral"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#0F0F0F] mb-1.5">
                      Full Name <span className="text-[#E50914]">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#E50914] focus:ring-2 focus:ring-[#E50914]/20 outline-none transition"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0F0F0F] mb-1.5">
                      Phone <span className="text-[#E50914]">*</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="604 555 1234"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#E50914] focus:ring-2 focus:ring-[#E50914]/20 outline-none transition"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F0F0F] mb-1.5">
                    Email <span className="text-[#E50914]">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#E50914] focus:ring-2 focus:ring-[#E50914]/20 outline-none transition"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F0F0F] mb-1.5">
                    Service needed
                  </label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#E50914] focus:ring-2 focus:ring-[#E50914]/20 outline-none bg-white transition"
                    data-testid="contact-service"
                  >
                    <option value="">Select a service (optional)</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F0F0F] mb-1.5">
                    How can we help? <span className="text-[#E50914]">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project, issue, or rebate question..."
                    rows={5}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#E50914] focus:ring-2 focus:ring-[#E50914]/20 outline-none transition resize-y"
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-coral w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-xs text-[#64748B] text-center">
                  By submitting this form, you agree to be contacted by Corona Gas about your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
